/**
 * Persist draft location tags after Create Request first save (#999).
 */

import { api } from './api';
import { compareLocationTagCreationOrder } from '../utils/groupUnitsByLocationThenStatus';

import type { LocationTagDraft } from '../contexts/LocationTagsDraftContext';
import type { LocationTagResponse } from '../types/locationTag';

export async function flushDraftLocationTags(
  requestId: string | number,
  drafts: LocationTagDraft[],
): Promise<Map<number, number>> {
  const mapping = new Map<number, number>();
  if (drafts.length === 0) {
    return mapping;
  }

  const requestIdStr = String(requestId);
  const ordered = [...drafts].sort((a, b) =>
    compareLocationTagCreationOrder(a.clientId, b.clientId),
  );

  for (const draft of ordered) {
    const response = await api.post<LocationTagResponse>(
      `/requests/${requestIdStr}/location-tags`,
      {
        name: draft.name,
      },
    );
    const createdId = response.locationTag?.id;
    if (createdId != null) {
      mapping.set(draft.clientId, createdId);
    }
  }

  return mapping;
}

export function remapDraftLocationTagIds<
  T extends { locationTagId?: number | null },
>(items: readonly T[], clientIdToServerId: ReadonlyMap<number, number>): T[] {
  return items.map(item => {
    const tagId = item.locationTagId;
    if (tagId == null || tagId >= 0) {
      return item;
    }
    const mapped = clientIdToServerId.get(tagId);
    return {
      ...item,
      locationTagId: mapped ?? null,
    };
  });
}

/** @deprecated Use remapDraftLocationTagIds */
export const remapRackDraftLocationTagIds = remapDraftLocationTagIds;

export type FlushDraftLocationTagsResult = {
  warning: string | null;
  clientIdToServerId: Map<number, number>;
};

/** Returns a user-facing warning label when flush fails (#999 Story 1.3). */
export async function flushDraftLocationTagsWithWarning(
  requestId: string | number,
  drafts: LocationTagDraft[],
  clearDraftTags: () => void,
): Promise<FlushDraftLocationTagsResult> {
  if (drafts.length === 0) {
    return { warning: null, clientIdToServerId: new Map() };
  }

  try {
    const clientIdToServerId = await flushDraftLocationTags(requestId, drafts);
    clearDraftTags();
    return { warning: null, clientIdToServerId };
  } catch (error) {
    console.error('Failed to save draft location tags:', error);
    return { warning: 'location tags', clientIdToServerId: new Map() };
  }
}
